import React, { useState, useEffect } from 'react';
import { Link, RefreshCw, Info } from 'lucide-react';
import FormModal from './FormModal';
import { frameworkMapperApi } from '@domains/compliance/definition/api/frameworkMapper.api';
import { frameworkVersionsApi } from '@domains/compliance/definition/frameworkVersions/frameworkVersions.api';
import type {
  FrameworkMappingResponse,
  TemplateNode,
  CreateFrameworkMappingRequest,
  UpdateFrameworkMappingRequest
} from '@domains/compliance/definition/api/frameworkMapper.dto';
import type { FrameworkVersion } from '@domains/compliance/definition/frameworkVersions/frameworkVersions.dto';

interface FrameworkMappingModalProps {
  onClose: () => void;
  onSubmit: (mapping: CreateFrameworkMappingRequest | UpdateFrameworkMappingRequest) => Promise<void>;
  selectedMapping: FrameworkMappingResponse | null;
  initialData?: MappingFormData;
  onSuccess?: () => void;
}

interface MappingFormData {
  template_id: number;
  template_node_id: number;
  framework_id: number;
  framework_version_id: number;
  external_clause_ref: string;
  mapping_type: 'direct' | 'partial' | 'indirect';
  notes: string;
}

export const FrameworkMappingModal: React.FC<FrameworkMappingModalProps> = ({
  onClose,
  onSubmit,
  selectedMapping,
  initialData,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<MappingFormData>(
    selectedMapping ? {
      template_id: selectedMapping.template_node?.template_id || 0,
      template_node_id: selectedMapping.template_node_id || 0,
      framework_id: selectedMapping.framework_id || 0,
      framework_version_id: selectedMapping.framework_version_id || 0,
      external_clause_ref: selectedMapping.external_clause_ref || '',
      mapping_type: selectedMapping.mapping_type || 'direct',
      notes: selectedMapping.notes || '',
    } : (initialData || {
      template_id: 0,
      template_node_id: 0,
      framework_id: 0,
      framework_version_id: 0,
      external_clause_ref: '',
      mapping_type: 'direct',
      notes: '',
    })
  );

  const [templates, setTemplates] = useState<any[]>([]);
  const [templateNodes, setTemplateNodes] = useState<TemplateNode[]>([]);
  const [frameworkVersions, setFrameworkVersions] = useState<FrameworkVersion[]>([]);
  const [loading, _setLoading] = useState(false);
  const [loadingNodes, setLoadingNodes] = useState(false);

  useEffect(() => {
    loadTemplates();
    loadFrameworkVersions();
  }, []);

  useEffect(() => {
    if (formData.template_id) {
      loadTemplateNodes(formData.template_id);
    } else {
      setTemplateNodes([]);
    }
  }, [formData.template_id]);

  const loadTemplates = async () => {
    try {
      const data = await frameworkMapperApi.getTemplates();
      setTemplates(data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const loadTemplateNodes = async (templateId: number) => {
    setLoadingNodes(true);
    try {
      const data = await frameworkMapperApi.getTemplateNodes(templateId);
      setTemplateNodes(data || []);
    } catch (error) {
      console.error('Error loading template nodes:', error);
    } finally {
      setLoadingNodes(false);
    }
  };

  const loadFrameworkVersions = async () => {
    try {
      const data = await frameworkVersionsApi.getActiveFrameworkVersions();
      setFrameworkVersions(data || []);
    } catch (error) {
      console.error('Error loading framework versions:', error);
    }
  };

  const handleTemplateChange = (templateId: number) => {
    setFormData({ ...formData, template_id: templateId, template_node_id: 0 });
  };

  const handleNodeChange = (nodeId: number) => {
    setFormData({ ...formData, template_node_id: nodeId });
  };

  const handleFrameworkVersionChange = (versionId: number) => {
    const selectedVersion = frameworkVersions.find(v => v.id === versionId);
    setFormData({ ...formData, framework_version_id: versionId, framework_id: selectedVersion?.framework_id || 0 });
  };

  const getNodeTypeLabel = (nodeType: string) => {
    const labels: Record<string, string> = {
      'scope': 'Scope', 'section': 'Section', 'requirement': 'Requirement',
      'sub_requirement': 'Sub-Requirement', 'control': 'Control',
    };
    return labels[nodeType] || nodeType;
  };

  const handleSubmit = async () => {
    await onSubmit(formData);
    if (onSuccess) onSuccess();
    onClose();
  };

  const isCreate = !selectedMapping?.id;

  return (
    <FormModal
      title={isCreate ? 'Create Framework Mapping' : 'Edit Framework Mapping'}
      subtitle={isCreate
        ? 'Connect a template requirement to a compliance framework'
        : 'Modify this mapping connection'}
      icon={<Link className="text-white" size={24} />}
      onSubmit={handleSubmit}
      onCancel={onClose}
      submitText={isCreate ? 'Create Mapping' : 'Update Mapping'}
      cancelText="Cancel"
      loading={loading}
    >
      <div className="space-y-6">
        {/* Explanation */}
        {isCreate && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
            <div className="flex items-start gap-2">
              <Info size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-700">
                <strong>What is mapping?</strong> Mapping connects a template requirement (what evidence to collect)
                to a framework version (which standard requires it). When the framework is assigned to a company,
                mapped requirements become compliance pages the company must complete.
              </p>
            </div>
          </div>
        )}

        {/* Template + Node */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Template <span className="text-red-500">*</span></label>
            <select
              value={formData.template_id}
              onChange={(e) => handleTemplateChange(parseInt(e.target.value))}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
              disabled={loading}
            >
              <option value={0}>Select a template...</option>
              {templates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.name} {template.version ? `(${template.version})` : ''}
                </option>
              ))}
            </select>
            <p className="text-[10px] text-slate-400">Only published templates appear here</p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Requirement Node <span className="text-red-500">*</span></label>
            {loadingNodes ? (
              <div className="flex items-center justify-center py-3">
                <RefreshCw className="text-slate-600 animate-spin" size={20} />
                <p className="text-slate-600 font-medium ml-2 text-sm">Loading...</p>
              </div>
            ) : (
              <select
                value={formData.template_node_id}
                onChange={(e) => handleNodeChange(parseInt(e.target.value))}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
                disabled={!formData.template_id || loading}
              >
                <option value={0}>Select a node...</option>
                {templateNodes.map(node => (
                  <option key={node.id} value={node.id}>
                    {getNodeTypeLabel(node.node_type)}: {node.name}
                  </option>
                ))}
              </select>
            )}
            <p className="text-[10px] text-slate-400">The specific requirement that will become a compliance page</p>
          </div>
        </div>

        {/* Framework Version */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">Framework Version <span className="text-red-500">*</span></label>
          <select
            value={formData.framework_version_id}
            onChange={(e) => handleFrameworkVersionChange(parseInt(e.target.value))}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
            disabled={loading}
          >
            <option value={0}>Select a framework version...</option>
            {frameworkVersions.map(fv => (
              <option key={fv.id} value={fv.id}>
                {fv.framework_name || 'Framework'} — {fv.version}
              </option>
            ))}
          </select>
          <p className="text-[10px] text-slate-400">The compliance standard this requirement belongs to</p>
        </div>

        {/* Clause Ref + Mapping Type */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">External Clause Reference</label>
            <input
              type="text"
              value={formData.external_clause_ref}
              onChange={(e) => setFormData({ ...formData, external_clause_ref: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
              placeholder="e.g., A.5.1 or CC6.1"
              disabled={loading}
            />
            <p className="text-[10px] text-slate-400">The clause number from the original standard document (optional)</p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Mapping Type</label>
            <select
              value={formData.mapping_type}
              onChange={(e) => setFormData({ ...formData, mapping_type: e.target.value as 'direct' | 'partial' | 'indirect' })}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
              disabled={loading}
            >
              <option value="direct">Direct — Fully satisfies the requirement</option>
              <option value="partial">Partial — Partially addresses the requirement</option>
              <option value="indirect">Indirect — Supports the requirement indirectly</option>
            </select>
            <p className="text-[10px] text-slate-400">How closely this template node maps to the framework requirement</p>
          </div>
        </div>

        {/* Notes */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
            rows={2}
            placeholder="Optional context about this mapping..."
            disabled={loading}
          />
        </div>
      </div>
    </FormModal>
  );
};
